import { z } from "zod";

import { activityFormatSchema } from "./library.js";
import { activitySessionSchema } from "./scheduling.js";

export const MAX_ACTIVITY_MATERIALS = 30;
export const MAX_RICH_TEXT_CONTAINER_DEPTH = 8;
export const MAX_RICH_TEXT_NODES = 2_000;
export const MAX_IMAGE_BYTES = 10 * 1_024 * 1_024;
export const MAX_IMAGE_DIMENSION = 8_192;
export const MAX_IMAGE_PIXELS = 40_000_000;
export const MAX_FILE_BYTES = 25 * 1_024 * 1_024;
export const ALLOWED_IMAGE_TYPES = ["image/jpeg", "image/png", "image/webp", "image/gif"] as const;
export const ALLOWED_FILE_TYPES = [
  "application/pdf",
  "text/plain",
  "text/markdown",
  "application/msword",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  "application/vnd.ms-powerpoint",
  "application/vnd.openxmlformats-officedocument.presentationml.presentation",
] as const;

export const activityTitleSchema = z
  .string()
  .trim()
  .min(1, "Give this activity a clear title.")
  .max(160, "Keep the title under 160 characters.");
export const activitySummaryInputSchema = z
  .string()
  .trim()
  .max(500, "Keep the learning intention under 500 characters.");
export const estimatedMinutesSchema = z.coerce
  .number()
  .int("Use a whole number of minutes.")
  .min(1, "Plan at least 1 minute.")
  .max(1_440, "Keep the estimate within 24 hours.");
export const ratingSchema = z.coerce.number().int().min(1).max(5);
export const resourceTypeSchema = z.enum(["article", "video", "course", "training"]);
export const uploadKindSchema = z.enum(["image", "file"]);
export const uploadStatusSchema = z.enum(["pending", "ready", "failed", "deleted"]);

export const safeHttpUrlSchema = z
  .string()
  .trim()
  .max(2_048, "Keep the URL under 2,048 characters.")
  .url("Enter a complete URL.")
  .refine((value) => {
    const protocol = new URL(value).protocol;
    return protocol === "https:" || protocol === "http:";
  }, "Only http and https links are supported.");

export type RichTextMark =
  | { type: "bold" }
  | { type: "italic" }
  | { type: "link"; attrs: { href: string } };

export type RichTextNode =
  | { type: "text"; text: string; marks?: RichTextMark[] | undefined }
  | { type: "paragraph"; content?: RichTextNode[] | undefined }
  | { type: "heading"; attrs: { level: 2 | 3 }; content?: RichTextNode[] | undefined }
  | { type: "bulletList"; content?: RichTextNode[] | undefined }
  | { type: "orderedList"; content?: RichTextNode[] | undefined }
  | { type: "listItem"; content?: RichTextNode[] | undefined }
  | { type: "blockquote"; content?: RichTextNode[] | undefined }
  | { type: "hardBreak" };

export type RichTextDocument = {
  type: "doc";
  content: RichTextNode[];
};

const richTextMarkSchema: z.ZodType<RichTextMark> = z.union([
  z.object({ type: z.literal("bold") }).strict(),
  z.object({ type: z.literal("italic") }).strict(),
  z
    .object({
      type: z.literal("link"),
      attrs: z.object({ href: safeHttpUrlSchema }).strict(),
    })
    .strict(),
]);

const richTextNodeSchema: z.ZodType<RichTextNode> = z.lazy(() =>
  z.discriminatedUnion("type", [
    z
      .object({
        type: z.literal("text"),
        text: z.string().max(10_000),
        marks: z.array(richTextMarkSchema).max(8).optional(),
      })
      .strict(),
    z
      .object({
        type: z.literal("paragraph"),
        content: z.array(richTextNodeSchema).max(250).optional(),
      })
      .strict(),
    z
      .object({
        type: z.literal("heading"),
        attrs: z.object({ level: z.union([z.literal(2), z.literal(3)]) }).strict(),
        content: z.array(richTextNodeSchema).max(250).optional(),
      })
      .strict(),
    z
      .object({
        type: z.literal("bulletList"),
        content: z.array(richTextNodeSchema).max(250).optional(),
      })
      .strict(),
    z
      .object({
        type: z.literal("orderedList"),
        content: z.array(richTextNodeSchema).max(250).optional(),
      })
      .strict(),
    z
      .object({
        type: z.literal("listItem"),
        content: z.array(richTextNodeSchema).max(250).optional(),
      })
      .strict(),
    z
      .object({
        type: z.literal("blockquote"),
        content: z.array(richTextNodeSchema).max(250).optional(),
      })
      .strict(),
    z.object({ type: z.literal("hardBreak") }).strict(),
  ]),
);

const richTextDocumentStructureSchema: z.ZodType<RichTextDocument> = z
  .object({
    type: z.literal("doc"),
    content: z.array(richTextNodeSchema).max(500),
  })
  .strict();

export const richTextDocumentSchema: z.ZodType<RichTextDocument> = z
  .unknown()
  .refine(hasValidRichTextBudget, {
    message: `Rich text is limited to ${MAX_RICH_TEXT_CONTAINER_DEPTH.toString()} nested container levels and ${MAX_RICH_TEXT_NODES.toLocaleString("en-US")} nodes.`,
    abort: true,
  })
  .pipe(richTextDocumentStructureSchema);

const richTextContainerTypes = new Set([
  "paragraph",
  "heading",
  "bulletList",
  "orderedList",
  "listItem",
  "blockquote",
]);

function hasValidRichTextBudget(value: unknown): boolean {
  if (!isRecord(value) || value["type"] !== "doc" || !Array.isArray(value["content"])) {
    return true;
  }
  if (value["content"].length > MAX_RICH_TEXT_NODES) return false;

  const pending: Array<{ value: unknown; containerDepth: number }> = [];
  for (let index = value["content"].length - 1; index >= 0; index -= 1) {
    pending.push({ value: value["content"][index], containerDepth: 0 });
  }

  let nodeCount = 0;
  while (pending.length > 0) {
    const current = pending.pop();
    if (!current) break;
    nodeCount += 1;
    if (nodeCount > MAX_RICH_TEXT_NODES) return false;
    if (
      !isRecord(current.value) ||
      typeof current.value["type"] !== "string" ||
      !richTextContainerTypes.has(current.value["type"])
    ) {
      continue;
    }

    const containerDepth = current.containerDepth + 1;
    if (containerDepth > MAX_RICH_TEXT_CONTAINER_DEPTH) return false;

    const content = current.value["content"];
    if (!Array.isArray(content)) continue;
    if (nodeCount + pending.length + content.length > MAX_RICH_TEXT_NODES) return false;
    for (let index = content.length - 1; index >= 0; index -= 1) {
      pending.push({ value: content[index], containerDepth });
    }
  }

  return true;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

const materialBaseSchema = z.object({ id: z.uuid() });

export const linkMaterialInputSchema = materialBaseSchema
  .extend({
    kind: z.literal("link"),
    url: safeHttpUrlSchema,
    title: z.string().trim().min(1, "Give this resource a title.").max(200),
    provider: z.string().trim().min(1, "Name the resource provider.").max(100),
    resourceType: resourceTypeSchema,
    thumbnailUrl: safeHttpUrlSchema.nullable(),
    durationMinutes: z.number().int().positive().max(1_440).nullable(),
  })
  .strict();

export const richTextMaterialInputSchema = materialBaseSchema
  .extend({
    kind: z.literal("rich_text"),
    document: richTextDocumentSchema,
  })
  .strict();

export const imageMaterialInputSchema = materialBaseSchema
  .extend({
    kind: z.literal("image"),
    fileId: z.uuid(),
    altText: z.string().trim().max(300),
    decorative: z.boolean(),
  })
  .strict()
  .superRefine((value, context) => {
    if (!value.decorative && !value.altText) {
      context.addIssue({
        code: "custom",
        path: ["altText"],
        message: "Describe the image or mark it as decorative.",
      });
    }
  });

export const fileMaterialInputSchema = materialBaseSchema
  .extend({
    kind: z.literal("file"),
    fileId: z.uuid(),
  })
  .strict();

export const activityMaterialInputSchema = z.discriminatedUnion("kind", [
  linkMaterialInputSchema,
  richTextMaterialInputSchema,
  imageMaterialInputSchema,
  fileMaterialInputSchema,
]);

export const activityWriteSchema = z
  .object({
    title: activityTitleSchema,
    summary: activitySummaryInputSchema,
    collectionId: z.uuid().nullable(),
    estimatedMinutes: estimatedMinutesSchema,
    materials: z.array(activityMaterialInputSchema).max(MAX_ACTIVITY_MATERIALS),
  })
  .strict()
  .superRefine((value, context) => {
    const seenMaterialIds = new Set<string>();
    const seenFileIds = new Set<string>();
    for (const [index, material] of value.materials.entries()) {
      if (seenMaterialIds.has(material.id)) {
        context.addIssue({
          code: "custom",
          path: ["materials", index, "id"],
          message: "Each material block must have a unique identifier.",
        });
      }
      seenMaterialIds.add(material.id);
      if (material.kind === "image" || material.kind === "file") {
        if (seenFileIds.has(material.fileId)) {
          context.addIssue({
            code: "custom",
            path: ["materials", index, "fileId"],
            message: "Each uploaded file can be attached only once.",
          });
        }
        seenFileIds.add(material.fileId);
      }
    }
  });

export const createActivityInputSchema = activityWriteSchema.extend({
  idempotencyKey: z.uuid(),
});
export const updateActivityInputSchema = activityWriteSchema.extend({
  version: z.number().int().positive(),
});

export const editorCollectionSchema = z.object({
  id: z.uuid(),
  name: z.string(),
});

export const uploadFileSchema = z.object({
  id: z.uuid(),
  kind: uploadKindSchema,
  originalFilename: z.string(),
  contentType: z.string(),
  size: z.number().int().nonnegative(),
  status: uploadStatusSchema,
  contentUrl: z.string().nullable(),
});

export const activityMaterialSchema = z.discriminatedUnion("kind", [
  linkMaterialInputSchema,
  richTextMaterialInputSchema,
  imageMaterialInputSchema.extend({ file: uploadFileSchema }),
  fileMaterialInputSchema.extend({ file: uploadFileSchema }),
]);

export const activityDocumentSchema = z.object({
  id: z.uuid(),
  version: z.number().int().positive(),
  title: z.string(),
  summary: z.string().nullable(),
  collection: editorCollectionSchema.nullable(),
  estimatedMinutes: z.number().int().positive(),
  interest: ratingSchema.nullable(),
  difficulty: ratingSchema.nullable(),
  format: activityFormatSchema,
  materials: z.array(activityMaterialSchema),
  createdAt: z.string(),
  updatedAt: z.string(),
});

export const activityNoteSchema = z.object({
  id: z.uuid(),
  document: richTextDocumentSchema,
  version: z.number().int().positive(),
  createdAt: z.string(),
  updatedAt: z.string(),
});

export const completionEventSchema = z.object({
  id: z.uuid(),
  activityId: z.uuid(),
  sessionId: z.uuid(),
  completedAt: z.string(),
  reversedAt: z.string().nullable(),
});

export const activityDetailSchema = activityDocumentSchema.extend({
  favoriteAt: z.string().nullable(),
  archivedAt: z.string().nullable(),
  note: activityNoteSchema.nullable(),
  completionHistory: z.array(completionEventSchema),
  sessions: z.array(activitySessionSchema),
  completedSessionCount: z.number().int().nonnegative(),
});

export const saveActivityNoteInputSchema = z
  .object({
    version: z.number().int().positive().nullable(),
    document: richTextDocumentSchema,
  })
  .strict();
export const activityNoteMutationResponseSchema = z.object({
  note: activityNoteSchema.nullable(),
});
export const setActivityFavoriteInputSchema = z.object({ favorite: z.boolean() }).strict();
export const activityFavoriteResponseSchema = z.object({
  activityId: z.uuid(),
  favoriteAt: z.string().nullable(),
});
export const completionIdParamsSchema = z.object({
  activityId: z.uuid(),
  completionId: z.uuid(),
});
export const activityMaterialParamsSchema = z.object({
  activityId: z.uuid(),
  materialId: z.uuid(),
});
export const externalMaterialAvailabilitySchema = z.object({
  availability: z.literal("available"),
});
export const activityMutationResponseSchema = z.object({
  activity: activityDocumentSchema,
  replayed: z.boolean(),
});

export const activityEditorOptionsSchema = z.object({
  collections: z.array(editorCollectionSchema),
});

export const resourceMetadataInputSchema = z.object({ url: safeHttpUrlSchema }).strict();
export const resourceMetadataSchema = z.object({
  url: safeHttpUrlSchema,
  title: z.string(),
  provider: z.string(),
  resourceType: resourceTypeSchema,
  thumbnailUrl: safeHttpUrlSchema.nullable(),
  durationMinutes: z.number().int().positive().nullable(),
});

export const duplicateLinkCheckInputSchema = z
  .object({
    links: z
      .array(
        z
          .object({
            materialId: z.uuid(),
            url: safeHttpUrlSchema,
          })
          .strict(),
      )
      .max(MAX_ACTIVITY_MATERIALS),
    excludeActivityId: z.uuid().nullable(),
  })
  .strict()
  .superRefine((value, context) => {
    const materialIds = new Set<string>();
    for (const [index, link] of value.links.entries()) {
      if (materialIds.has(link.materialId)) {
        context.addIssue({
          code: "custom",
          path: ["links", index, "materialId"],
          message: "Each link material can be checked only once.",
        });
      }
      materialIds.add(link.materialId);
    }
  });

export const duplicateLinkCheckResponseSchema = z.object({
  links: z.array(
    z.object({
      materialId: z.uuid(),
      matches: z.array(
        z.object({
          activityId: z.uuid(),
          title: z.string(),
        }),
      ),
    }),
  ),
});

export const createUploadInputSchema = z
  .object({
    kind: uploadKindSchema,
    originalFilename: z.string().trim().min(1).max(255),
    contentType: z.string().trim().min(1).max(120),
    size: z.number().int().positive(),
    checksum: z.string().regex(/^[a-f0-9]{64}$/, "Provide a SHA-256 checksum."),
  })
  .strict()
  .superRefine((value, context) => {
    const allowed =
      value.kind === "image"
        ? ALLOWED_IMAGE_TYPES.includes(value.contentType as (typeof ALLOWED_IMAGE_TYPES)[number])
        : ALLOWED_FILE_TYPES.includes(value.contentType as (typeof ALLOWED_FILE_TYPES)[number]);
    const limit = value.kind === "image" ? MAX_IMAGE_BYTES : MAX_FILE_BYTES;
    if (!allowed) {
      context.addIssue({
        code: "custom",
        path: ["contentType"],
        message:
          value.kind === "image"
            ? "Use a JPEG, PNG, WebP, or GIF image."
            : "Use a PDF, text, Markdown, Word, or PowerPoint file.",
      });
    }
    if (value.size > limit) {
      context.addIssue({
        code: "custom",
        path: ["size"],
        message:
          value.kind === "image"
            ? "Keep images at or below 10 MB."
            : "Keep files at or below 25 MB.",
      });
    }
  });

export const uploadIdParamsSchema = z.object({ uploadId: z.uuid() });
export const uploadTargetSchema = z.object({
  method: z.literal("PUT"),
  url: z.string(),
  headers: z.record(z.string(), z.string()),
  expiresAt: z.string(),
});
export const createUploadResponseSchema = z.object({
  file: uploadFileSchema,
  upload: uploadTargetSchema,
});
export const confirmUploadResponseSchema = z.object({ file: uploadFileSchema });

export type ActivityWrite = z.infer<typeof activityWriteSchema>;
export type CreateActivityInput = z.infer<typeof createActivityInputSchema>;
export type UpdateActivityInput = z.infer<typeof updateActivityInputSchema>;
export type ActivityMaterialInput = z.infer<typeof activityMaterialInputSchema>;
export type ActivityMaterial = z.infer<typeof activityMaterialSchema>;
export type ActivityDocument = z.infer<typeof activityDocumentSchema>;
export type ActivityNote = z.infer<typeof activityNoteSchema>;
export type CompletionEvent = z.infer<typeof completionEventSchema>;
export type ActivityDetail = z.infer<typeof activityDetailSchema>;
export type SaveActivityNoteInput = z.infer<typeof saveActivityNoteInputSchema>;
export type ActivityNoteMutationResponse = z.infer<typeof activityNoteMutationResponseSchema>;
export type SetActivityFavoriteInput = z.infer<typeof setActivityFavoriteInputSchema>;
export type ActivityFavoriteResponse = z.infer<typeof activityFavoriteResponseSchema>;
export type ActivityMutationResponse = z.infer<typeof activityMutationResponseSchema>;
export type ActivityEditorOptions = z.infer<typeof activityEditorOptionsSchema>;
export type ResourceMetadata = z.infer<typeof resourceMetadataSchema>;
export type DuplicateLinkCheckInput = z.infer<typeof duplicateLinkCheckInputSchema>;
export type DuplicateLinkCheckResponse = z.infer<typeof duplicateLinkCheckResponseSchema>;
export type CreateUploadInput = z.infer<typeof createUploadInputSchema>;
export type UploadFile = z.infer<typeof uploadFileSchema>;
export type UploadTarget = z.infer<typeof uploadTargetSchema>;
export type CreateUploadResponse = z.infer<typeof createUploadResponseSchema>;
export type ConfirmUploadResponse = z.infer<typeof confirmUploadResponseSchema>;
